/** Color palette driving the whole deck design (cards, gradients, page). */
export interface DesignPalette {
  blueLight: string;
  blue: string;
  blueDark: string;
  navy: string;
  navyDeep: string;
  cream: string;
  accent: string;
  text: string;
  textMuted: string;
  border: string;
  gradient: string;
  pageGradient: string;
}

export interface DesignTemplate {
  id: string;
  name: string;
  description: string;
  palette: DesignPalette;
}

export const DEFAULT_DESIGN_ID = 'ocean-blue';

export const designTemplates: DesignTemplate[] = [
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    description: 'The classic Spotdove look: sky-blue gradient, cream panels, and a calm, trustworthy feel.',
    palette: {
      blueLight: '#8BC7E0',
      blue: '#44A1EA',
      blueDark: '#4779BD',
      navy: '#232E50',
      navyDeep: '#051554',
      cream: '#FAFAF7',
      accent: '#8BC7E0',
      text: '#051554',
      textMuted: '#4779BD',
      border: '#CFE3F1',
      gradient: 'linear-gradient(160deg, #8BC7E0 0%, #44A1EA 100%)',
      pageGradient: 'linear-gradient(180deg, #EAF4FA 0%, #D4E8F7 100%)',
    },
  },
  {
    id: 'sunset-coral',
    name: 'Sunset Coral',
    description: 'Warm coral and amber tones with a soft peach canvas. Energetic and optimistic.',
    palette: {
      blueLight: '#FBC4A0',
      blue: '#F0754F',
      blueDark: '#C74E31',
      navy: '#5A2A1C',
      navyDeep: '#3D1409',
      cream: '#FFF9F4',
      accent: '#F9B08A',
      text: '#3D1409',
      textMuted: '#A65B3F',
      border: '#F5D9C8',
      gradient: 'linear-gradient(160deg, #FBC4A0 0%, #F0754F 100%)',
      pageGradient: 'linear-gradient(180deg, #FDF1E7 0%, #FADDC9 100%)',
    },
  },
  {
    id: 'forest-emerald',
    name: 'Forest Emerald',
    description: 'Deep greens with a fresh mint gradient. Grounded, sustainable, and confident.',
    palette: {
      blueLight: '#8FD6B4',
      blue: '#2E9E6B',
      blueDark: '#1F7350',
      navy: '#1B3B2D',
      navyDeep: '#0B2419',
      cream: '#F8FBF7',
      accent: '#8FD6B4',
      text: '#0B2419',
      textMuted: '#3E7A5E',
      border: '#CDE8D9',
      gradient: 'linear-gradient(160deg, #8FD6B4 0%, #2E9E6B 100%)',
      pageGradient: 'linear-gradient(180deg, #ECF6EF 0%, #D5EBDD 100%)',
    },
  },
  {
    id: 'midnight-violet',
    name: 'Midnight Violet',
    description: 'Dark navy cards with an indigo-violet gradient. Bold, premium, made for the big stage.',
    palette: {
      blueLight: '#8D8AE6',
      blue: '#6C63D2',
      blueDark: '#524BAF',
      navy: '#3B3670',
      navyDeep: '#12102E',
      cream: '#191633',
      accent: '#8D8AE6',
      text: '#F2F1FC',
      textMuted: '#B3AFE0',
      border: '#39346B',
      gradient: 'linear-gradient(160deg, #8D8AE6 0%, #524BAF 100%)',
      pageGradient: 'linear-gradient(180deg, #221E45 0%, #12102E 100%)',
    },
  },
  {
    id: 'paper-ink',
    name: 'Paper & Ink',
    description: 'Minimal monochrome: charcoal ink on warm paper. Quiet, editorial, lets the content lead.',
    palette: {
      blueLight: '#C9C9C4',
      blue: '#4A4A46',
      blueDark: '#2C2C29',
      navy: '#33332F',
      navyDeep: '#1A1A18',
      cream: '#FCFBF7',
      accent: '#D8D6CE',
      text: '#1A1A18',
      textMuted: '#6E6E67',
      border: '#E2E0D8',
      gradient: 'linear-gradient(160deg, #C9C9C4 0%, #4A4A46 100%)',
      pageGradient: 'linear-gradient(180deg, #F3F2EC 0%, #E5E3DB 100%)',
    },
  },
];

export const getDesignTemplate = (id: string): DesignTemplate | undefined =>
  designTemplates.find((design) => design.id === id);

export const getDesignOrDefault = (id?: string | null): DesignTemplate =>
  (id ? getDesignTemplate(id) : undefined) ??
  (getDesignTemplate(DEFAULT_DESIGN_ID) as DesignTemplate);
